import { usePlansByIdsQuery, usePdpPlansByBid } from '@coverright/data-access/medicare';
import { GetDefaultPlansYear, GetNextPlansYear } from '@coverright/utils';
import { addDetailsToMGPlanList } from '@coverright/data-access/medigap';
import { ComparablePlan as GlobalComparablePlan, ComparablePlanType, PlanInput } from '@coverright/data-access/types/medicare';
import { useMemo } from 'react';
import { useMedigapPlansByBid } from './use-medigap-plans-by-bid';

type ComparablePlan = GlobalComparablePlan & {
  aiSuggested?: boolean;
}

type AppContext  = [
  data: {mgSavedPlans?: ComparablePlan[], maSavedPlans?: ComparablePlan[], pdpSavedPlans?: ComparablePlan[], medicareQuoteId?: any},
  saveState: (data: any) => void,
  loaded: boolean
]

export function usePlansForCompare(appContext: AppContext, medicareQuoteId?: string, clientId?: string) {
  const [data, saveState ] = appContext;


  const maConverted: PlanInput[] = useMemo(
    () => {
      const plans = Array.isArray(data.maSavedPlans) ? data.maSavedPlans : []
      return plans.map((p) => ({
        bidId: p.bidId,
        planYear: p.planYear || undefined,
        zip: p.zip,
        county: p.county,
        fullYear: p.planYear === GetNextPlansYear()
      }))
    },
    [data.maSavedPlans]
  );

  const maPlans = usePlansByIdsQuery({
    variables: {
      quoteId: medicareQuoteId || data?.medicareQuoteId,
      plans: maConverted
    },
    skip: !maConverted?.length
  });

  const pdpConverted = useMemo(
    () => {
      const plans = Array.isArray(data.pdpSavedPlans) ? data.pdpSavedPlans : []
      return plans.map((p) => ({
        bidId: p.bidId,
        planYear: p.planYear || GetDefaultPlansYear(),
        zip: p.zip,
        countyName: p.county,
      }))
    },
    [data.pdpSavedPlans]
  );

  const pdpPlans = usePdpPlansByBid(pdpConverted, clientId);

  const mgConverted = useMemo(
    () => {
      const plans = Array.isArray(data.mgSavedPlans) ? data.mgSavedPlans : []
      return plans.map((p) => p.bidId)
    },
    [data.mgSavedPlans]
  );

  const mgPlans = useMedigapPlansByBid(mgConverted);

  const removePlan = (type: ComparablePlanType, id: string) => {
    const key = `${type.toLowerCase()}SavedPlans` as keyof AppContext[0];
    const savedPlans = Array.isArray(data[key]) ? data[key] : [];
    if (savedPlans) {
      saveState({
        [`${type.toLowerCase()}SavedPlans`]: savedPlans.filter(
          (p: ComparablePlan) => p.bidId !== id
        ),
      });
    }
  };

  const removePlans = (items: Array<{ type: ComparablePlanType; id: string }>) => {
    const grouped = items.reduce((acc, item) => {
      const key = `${item.type.toLowerCase()}SavedPlans`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item.id);
      return acc;
    }, {} as Record<string, string[]>);

    const result: Partial<AppContext[0]> = {};

    for (const [key, ids] of Object.entries(grouped)) {
      const savedPlans = Array.isArray(data[key as keyof AppContext[0]]) ? data[key as keyof AppContext[0]] : [];
      const idSet = new Set(ids);
      result[key as keyof AppContext[0]] = (savedPlans as ComparablePlan[]).filter(p => !idSet.has(p.bidId));
    }
    saveState(result);
  };

  const savePlan = (plan: ComparablePlan) => {
    const key = `${plan.type.toLowerCase()}SavedPlans` as keyof AppContext[0];
    const savedPlans = Array.isArray(data[key]) ? data[key] : [];
    saveState({
      [`${plan.type.toLowerCase()}SavedPlans`]: [
        ...savedPlans!.filter((p: ComparablePlan) => p.bidId !== plan.bidId),
        plan,
      ],
    });
  };

  const savePlans = (plans: ComparablePlan[]) => {
    const grouped = plans.reduce((acc, p) => {
      const key = `${p.type.toLowerCase()}SavedPlans`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(p);
      return acc;
    }, {} as Record<string, ComparablePlan[]>);

    const result: Partial<AppContext[0]> = {};

    for (const [key, newPlans] of Object.entries(grouped)) {
      const savedPlans = Array.isArray(data[key as keyof AppContext[0]]) ? data[key as keyof AppContext[0]] : [];
      const newBidIds = new Set(newPlans.map(p => p.bidId));

      result[key as keyof AppContext[0]] = [
        ...(savedPlans as ComparablePlan[]).filter(p => !newBidIds.has(p.bidId)),
        ...newPlans,
      ];
    }

    saveState(result);
  };

  const isPlanSaved = (plan: ComparablePlan) => {
    const key = `${plan.type.toLowerCase()}SavedPlans` as keyof AppContext[0];
    const savedPlans = Array.isArray(data[key]) ? data[key] : [];
    return savedPlans!.some((p: ComparablePlan) => p.bidId === plan.bidId);
  };

  return {
    maPlans: {
      called: maPlans.called,
      loading: maPlans.loading,
      refetch: maPlans.refetch,
      plans: maPlans.data?.plansByIds.map(p => ({
        ...p,
        zip: maConverted.find(c => c.bidId === p.bidId)?.zip,
        county: maConverted.find(c => c.bidId === p.bidId)?.county,
        isAiSuggested: data.maSavedPlans?.find(c => c.bidId === p.bidId)?.aiSuggested,
      })) || [],
      count: data.maSavedPlans?.length || 0,
    },
    pdpPlans: {
      ...pdpPlans,
      plans: pdpPlans.plans.map(p => ({
        ...p,
        zip: pdpConverted.find(c => c.bidId === p.bidId)?.zip,
        county: pdpConverted.find(c => c.bidId === p.bidId)?.countyName,
        isAiSuggested: data.pdpSavedPlans?.find(c => c.bidId === p.bidId)?.aiSuggested,
      })),
      count: data.pdpSavedPlans?.length || 0,
    },
    mgPlans: {
      ...mgPlans,
      plans: addDetailsToMGPlanList(mgPlans.plans).map(p => ({
        ...p,
        zip: (data.mgSavedPlans || []).find(c => c.bidId === p.key)?.zip,
        county: (data.mgSavedPlans || []).find(c => c.bidId === p.key)?.county,
        isAiSuggested: data.mgSavedPlans?.find(c => c.bidId === p.key)?.aiSuggested,
      })),
      count: data.mgSavedPlans?.length || 0,
    },
    removePlan,
    removePlans,
    savePlan,
    savePlans,
    isPlanSaved,
  };
}
